import DataQuery from "data/DataQuery";
import {
  Contract_EnergyType,
  Contract_Get,
  Contract_Prices,
  Contract_SpotProduct,
  Customer_PaymentMethods,
} from "data/endpoints";
import { DataLoadStatus } from "data/enums/dataLoadStatus";
import IRepository from "data/IRepository";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { useEffect, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import ContractEnergyDTO from "../dtos/ContractEnergyDTO";
import ContractFullDTO from "../dtos/ContractFullDTO";
import { ContractType } from "../enums/contractType";
import ContractFullModel from "features/contracts/models/ContractFullModel";
import PaymentMethodDTO from "features/payments/dtos/PaymentMethodDTO";
import PriceDTO from "../dtos/PriceDTO";
import SpotProductDTO from "../dtos/SpotProductDTO";
import { isEmpty } from "features/shared/helpers/objects";
import { useTranslation } from "react-i18next";

export const useContractDetailData = (
  contractId: string,
  customerId: string
): {
  contract: ContractFullModel;
  contractEnergy: ContractEnergyDTO;
  setContractEnergy: React.Dispatch<React.SetStateAction<ContractEnergyDTO>>;
  spotProduct: SpotProductDTO;
  loadStatus: DataLoadStatus;
} => {
  // DI
  const contractRepo = useInjection<IRepository<ContractFullDTO>>(
    IocTypes.Repository
  );
  const energyTypeRepo = useInjection<IRepository<ContractEnergyDTO>>(
    IocTypes.Repository
  );
  const paymentMethodRepo = useInjection<IRepository<PaymentMethodDTO>>(
    IocTypes.Repository
  );
  const priceRepo = useInjection<IRepository<PriceDTO>>(
    IocTypes.Repository
  );
  const spotRepo = useInjection<IRepository<SpotProductDTO>>(
    IocTypes.Repository
  );

  // translations
    const { i18n } = useTranslation();
  

  // state
  //const [contract, setContract] = useState<ContractFullDTO>({});
  const [contract, setContract] = useState<ContractFullModel>({ contract: {},paymentMethod: [], prices: [] });
  const [contractEnergy, setContractEnergy] = useState<ContractEnergyDTO>({});
  const [spotProduct, setSpotProduct] = useState<SpotProductDTO>({});
  const [loadStatus, setLoadStatus] = useState(DataLoadStatus.BeforeFirstLoad); // indication if data was loaded for the first time

  // side effects
  // fetch data for contract detail
  useEffect(() => {
    // fetch info about contract and contract energy
    try {
      trackPromise(fetchAndSetContractData());
    } catch (error) {
      setLoadStatus("LoadError");
    }
  }, [contractId, i18n.language]);

  // helpers
  const fetchAndSetContractData = async () => {
    //Fetch
    const result = await Promise.all([
      fetchContract(),
      fetchPaymentMethods(),
      fetchPrices()
    ]);

    const contract = result[0];
    const paymentMethods = result[1];
    const prices = result[2];

    // Namapovani  na model
    const contractFullModel = mapContractFullModel(contract, paymentMethods, prices);

    // Set
    if (contractFullModel && contractFullModel.contract.contractType) {
      let contractEnergy = await fetchContractEnergyType(
        contractFullModel.contract.contractType
      );
      let spotProduct = await fetchSpotProduct(contractFullModel.contract);

      setContract(contractFullModel);
      setContractEnergy(contractEnergy);
      setSpotProduct(spotProduct);
    }

    setLoadStatus("AfterFirstLoad");
  };

  const fetchContract = (): Promise<ContractFullDTO> => {
    let query: DataQuery = {
      url: Contract_Get,
      routeParams: getRouteParams("contractId", contractId),
    };

    return contractRepo.getOne(query);
  };

  const fetchContractEnergyType = (
    contractType: ContractType
  ): Promise<ContractEnergyDTO> => {
    let routeParams = getRouteParams("contractId", contractId);
    routeParams.set("energyType", contractType);

    let query: DataQuery = {
      url: Contract_EnergyType,
      routeParams: routeParams,
    };

    return energyTypeRepo.getOne(query);
  };

  const fetchPaymentMethods = (): Promise<PaymentMethodDTO[]> => {
    const routeParams = getRouteParams("customerId", customerId);

    const params = new URLSearchParams();
    params.set("ContractId", contractId);
    //params.set("Currency", "CZK");

    const query: DataQuery = {
      url: Customer_PaymentMethods,
      routeParams: routeParams,
      params: params,
    };

    return paymentMethodRepo.get(query);
  };

  const fetchPrices = (): Promise<PaymentMethodDTO[]> => {
    const routeParams = getRouteParams("contractId", contractId);
    const query: DataQuery = {
      url: Contract_Prices,
      routeParams: routeParams
    };

    return priceRepo.get(query);
  };

  const mapContractFullModel = (
    contract: ContractFullDTO,
    paymentMethodsModel: PaymentMethodDTO[],
    prices: PriceDTO[]
  ): ContractFullModel => {
        return {
          contract: {...contract},
          paymentMethod: [...paymentMethodsModel],
          prices: [...prices]
        }
  };

  const fetchSpotProduct = async (
    contract: ContractFullDTO
  ): Promise<SpotProductDTO> => {
    if (contract.contractSpot) {
      let routeParams = getRouteParams("contractId", contract.contractId!);

      let query: DataQuery = {
        url: Contract_SpotProduct,
        routeParams: routeParams,
      };

      let spotProduct = await spotRepo.getOne(query);

      if (spotProduct) {
        return spotProduct;
      }

      return {};
    }
    
    return {};
  };

  // return
  return { contract, contractEnergy, setContractEnergy, spotProduct, loadStatus };
};
